<template>
    <glm-layout margin-footer>
        <world-map :specials="$page.props.mapCountries" height="200px">
            <div class="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 flex items-center h-full justify-start">
                <div class="rounded-md max-w-lg inline-block p-2 bg-grey-700 text-white mt-3">
                    <h1 class="font-bold font-serif text-3xl">
                        Download
                    </h1>
                </div>
            </div>
        </world-map>

        <div class="section py-12">
            <page-content>
                <h2 class="font-semibold mb-3 text-glm-blue text-3xl font-serif">
                    Datasets
                </h2>

                <div class="mt-3 mb-4 text-sm shadow-sm rounded flex items-stretch justify-start">
                    <div class="p-2 bg-glm-blue text-blue-100 flex items-center justify-center rounded-l">
                        <font-awesome-icon
                            :icon="['fad', 'info']"
                            size="lg"
                            class="fa-fw"
                        ></font-awesome-icon>
                    </div>

                    <p class="p-2 bg-blue-100 text-glm-blue flex-1 rounded-r">
                        Please note that you follow our
                        <a
                            target="_blank"
                            :href="route('terms.show')"
                            class="font-bold hover:underline"
                        >
                            Terms of Service
                        </a>
                        if you use this data.
                    </p>
                </div>

                <h4 class="mb-2 font-medium mt-12">Documentation</h4>

                <div class="text-grey-800">
                    <a
                        v-for="(file, idx) in documentationFiles"
                        :key="idx"
                        class="flex items-stretch justify-start group outline-none shadow-sm"
                        :class="[idx > 0 ? 'mt-3' : '']"
                        target="_blank"
                        :href="file['url']"
                    >
                        <div
                            class="p-2 flex justify-center items-center bg-grey-300 group-hover:bg-grey-400 rounded-l"
                        >
                            <font-awesome-icon
                                :icon="getIcon(file['label'])"
                                size="lg"
                                class="fa-fw"
                            ></font-awesome-icon>
                        </div>

                        <div class="flex-1 p-2 bg-grey-100 group-hover:bg-grey-200 rounded-r">
                            {{ file['label'].replace('downloads/', '') }}
                        </div>
                    </a>
                </div>

                <h4 class="mb-2 font-medium mt-12">Replication code (Stata)</h4>

                <div class="text-grey-800">
                    <a
                        v-for="(file, idx) in replicationCode"
                        :key="idx"
                        class="flex items-stretch justify-start group outline-none shadow-sm"
                        :class="[idx > 0 ? 'mt-3' : '']"
                        target="_blank"
                        :href="file['url']"
                    >
                        <div
                            class="p-2 flex justify-center items-center bg-grey-300 group-hover:bg-grey-400 rounded-l"
                        >
                            <font-awesome-icon
                                :icon="getIcon(file['label'])"
                                size="lg"
                                class="fa-fw"
                            ></font-awesome-icon>
                        </div>

                        <div class="flex-1 p-2 bg-grey-100 group-hover:bg-grey-200 rounded-r">
                            {{ file['label'].replace('downloads/', '') }}
                        </div>
                    </a>
                </div>

                <h4 class="mb-2 font-medium mt-12">Data (.csv)</h4>

                <div class="text-grey-800">
                    <a
                        v-for="(file, idx) in csvFiles"
                        :key="idx"
                        class="flex items-stretch justify-start group outline-none shadow-sm"
                        :class="[idx > 0 ? 'mt-3' : '']"
                        target="_blank"
                        :href="file['url']"
                    >
                        <div
                            class="p-2 flex justify-center items-center bg-grey-300 group-hover:bg-grey-400 rounded-l"
                        >
                            <font-awesome-icon
                                :icon="getIcon(file['label'])"
                                size="lg"
                                class="fa-fw"
                            ></font-awesome-icon>
                        </div>

                        <div class="flex-1 p-2 bg-grey-100 group-hover:bg-grey-200 rounded-r">
                            {{ file['label'].replace('downloads/', '') }}
                        </div>
                    </a>
                </div>

                <h4 class="mb-2 font-medium mt-12">Data (.dta)</h4>

                <div class="text-grey-800">
                    <a
                        v-for="(file, idx) in dtaFiles"
                        :key="idx"
                        class="flex items-stretch justify-start group outline-none shadow-sm"
                        :class="[idx > 0 ? 'mt-3' : '']"
                        target="_blank"
                        :href="file['url']"
                    >
                        <div
                            class="p-2 flex justify-center items-center bg-grey-300 group-hover:bg-grey-400 rounded-l"
                        >
                            <font-awesome-icon
                                :icon="getIcon(file['label'])"
                                size="lg"
                                class="fa-fw"
                            ></font-awesome-icon>
                        </div>

                        <div class="flex-1 p-2 bg-grey-100 group-hover:bg-grey-200 rounded-r">
                            {{ file['label'].replace('downloads/', '') }}
                        </div>
                    </a>
                </div>

                <h4 class="mb-2 font-medium mt-12">Archive JWD Version</h4>

                <div class="text-grey-800">
                    <a
                        v-for="(file, idx) in archiveFiles"
                        :key="idx"
                        class="flex items-stretch justify-start group outline-none shadow-sm"
                        :class="[idx > 0 ? 'mt-3' : '']"
                        target="_blank"
                        :href="file['url']"
                    >
                        <div
                            class="p-2 flex justify-center items-center bg-grey-300 group-hover:bg-grey-400 rounded-l"
                        >
                            <font-awesome-icon
                                :icon="getIcon(file['label'])"
                                size="lg"
                                class="fa-fw"
                            ></font-awesome-icon>
                        </div>

                        <div class="flex-1 p-2 bg-grey-100 group-hover:bg-grey-200 rounded-r">
                            {{ file['label'].replace('downloads/', '') }}
                        </div>
                    </a>
                </div>
            </page-content>
        </div>
    </glm-layout>
</template>

<script>
import GlmLayout from '@/Layouts/GlmLayout';
import WorldMap from '@/Components/WorldMap';
import PageContent from '@/Components/PageContent';

export default {
    components: {
        PageContent,
        GlmLayout,
        WorldMap,
    },

    data()
    {
        return {};
    },

    computed: {
        documentationFiles()
        {
            return this.$page.props.files.filter(e => {
                return e['label'].includes('_user_manual');
            });
        },

        replicationCode()
        {
            return this.$page.props.files.filter(e => {
                return e['label'].includes('_replication_do_files');
            });
        },

        csvFiles()
        {
            return this.$page.props.files.filter(e => {
                return e['label'].includes('_csv');
            });
        },

        dtaFiles()
        {
            return this.$page.props.files.filter(e => {
                return e['label'].includes('_dta');
            });
        },

        archiveFiles()
        {
            return this.$page.props.files.filter(e => {
                return e['label'].includes('_archive_');
            });
        },

        otherFiles()
        {
            return this.$page.props.files.filter(e => {
                return !e['label'].includes('_archive_') && !e['label'].includes('_dta_') && !e['label'].includes(
                    '_csv_');
            });
        },
    },

    methods: {
        getIcon(file)
        {
            let fileName = file.split('/').pop();

            return fileName.endsWith('.zip') ? ['fad', 'file-archive'] : ['fad', 'file-download'];
        },
    },
};
</script>
